
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ProjectHeaderSection } from "@/components/project/ProjectHeaderSection";
import { ProjectTabs } from "@/components/project/ProjectTabs";
import type { Project } from "@/types/project";

export default function PublicProjectView() {
  const { token } = useParams();

  const { data: projectData, isLoading, error } = useQuery({
    queryKey: ["public-project", token],
    queryFn: async () => {
      if (!token) throw new Error("No token provided");

      console.log("Looking for project with token:", token);

      // Get the project by public share token
      const { data: project, error: projectError } = await supabase
        .from("projects")
        .select("*")
        .eq("public_share_token", token)
        .eq("is_public_shared", true)
        .eq("public_share_revoked", false)
        .maybeSingle();

      console.log("Project query result:", { project, projectError });

      if (projectError) {
        console.error("Database error:", projectError);
        throw new Error(`Database error: ${projectError.message}`);
      }

      if (!project) {
        console.log("No project found for token:", token);
        throw new Error("Project not found or sharing has been disabled");
      }

      // Check if share has expired
      if (project.public_share_expiration && new Date(project.public_share_expiration) < new Date()) {
        console.log("Share has expired:", project.public_share_expiration);
        throw new Error("Share link has expired");
      }

      // Update access count
      try {
        await supabase
          .from("public_project_shares")
          .upsert({ 
            project_id: project.id,
            share_token: token,
            access_count: 1,
            accessed_at: new Date().toISOString()
          }, {
            onConflict: 'project_id,share_token',
            ignoreDuplicates: false
          });
      } catch (accessError) {
        console.warn("Failed to update access count:", accessError);
      }

      // Convert the raw project data to match the Project type
      const typedProject: Project = {
        ...project,
        scout_photos: Array.isArray(project.scout_photos) 
          ? project.scout_photos 
          : project.scout_photos 
            ? JSON.parse(project.scout_photos as string) 
            : [],
        documents: Array.isArray(project.documents) 
          ? project.documents 
          : project.documents 
            ? JSON.parse(project.documents as string) 
            : [],
        custom_categories: Array.isArray(project.custom_categories) 
          ? project.custom_categories 
          : project.custom_categories 
            ? JSON.parse(project.custom_categories as string) 
            : [],
        shoot_dates: Array.isArray(project.shoot_dates) 
          ? project.shoot_dates 
          : project.shoot_dates 
            ? JSON.parse(project.shoot_dates as string) 
            : []
      };

      return { project: typedProject };
    },
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen site-gradient">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto space-y-6">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    console.error("Page error:", error);
    return (
      <div className="min-h-screen site-gradient flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto px-4">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto" />
          <h1 className="text-2xl font-bold text-white">Link Not Available</h1>
          <p className="text-gray-300">{error.message}</p>
          <Button 
            onClick={() => window.location.href = 'https://www.preptcine.com'}
            className="mt-4 bg-white text-indigo-900 hover:bg-gray-100"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Visit PREPT
          </Button>
        </div>
      </div>
    );
  }

  if (!projectData) {
    return (
      <div className="min-h-screen site-gradient flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto px-4">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto" />
          <h1 className="text-2xl font-bold text-white">No Data Available</h1>
          <p className="text-gray-300">Unable to load project data</p>
        </div>
      </div>
    );
  }

  const { project } = projectData;
  const canEdit = project.public_share_editable === true;

  return (
    <div className="min-h-screen site-gradient">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Guest Editing Notice */}
          {canEdit && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-green-300">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-sm font-medium">Guest editing enabled - You can make changes to this project</span>
              </div>
            </div>
          )}
          
          {/* Project Header */}
          <ProjectHeaderSection 
            project={project}
            isPublicView={true}
            canEdit={canEdit}
          />

          {/* Project Tabs */}
          <ProjectTabs 
            projectId={project.id}
            project={project}
            canEdit={canEdit}
            isPublicView={true}
          />

          {/* Footer */}
          <div className="mt-12 text-center py-8 border-t border-white/10">
            <p className="text-sm text-white/60 mb-4">
              Powered by PREPT - Professional Film Equipment Management
            </p>
            <Button 
              onClick={() => window.open('https://www.preptcine.com', '_blank')}
              variant="outline"
              className="border-white/20 text-white/80 hover:bg-white/10"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Get PREPT for Your Projects
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
